import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CriticalAlertsPanel = ({ alerts, onRefresh }) => {
  const getAlertIcon = (type) => {
    switch (type) {
      case 'suspended':
        return 'Ban';
      case 'expired':
        return 'Clock';
      case 'unauthorized':
        return 'AlertTriangle';
      case 'capacity':
        return 'Users';
      default:
        return 'AlertCircle';
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-destructive';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-card rounded-xl shadow-md border border-border">
      <div className="p-6 border-b border-border flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
          Critical Alerts
        </h2>
        <Button onClick={onRefresh} variant="ghost" size="sm">
          <Icon name="RefreshCw" size={18} />
        </Button>
      </div>

      <div className="p-6">
        {alerts?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
            <p className="text-muted-foreground">No critical alerts</p>
            <p className="text-xs text-muted-foreground mt-1">All systems operational</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {alerts?.map((alert) => (
              <div
                key={alert?.id}
                className="p-4 bg-background rounded-lg border-l-4 border-l-destructive"
              >
                <div className="flex items-start gap-3">
                  <Icon
                    name={getAlertIcon(alert?.type)}
                    size={20}
                    className={getAlertColor(alert?.severity)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-foreground">
                        {alert?.memberName}
                      </p>
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(alert?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {alert?.memberEmail}
                    </p>
                    <p className="text-sm text-foreground">
                      {alert?.message}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          alert?.severity === 'high' ?'bg-destructive/10 text-destructive'
                            : alert?.severity === 'medium' ?'bg-warning/10 text-warning' :'bg-muted text-muted-foreground'
                        }`}
                      >
                        {alert?.severity?.toUpperCase()} PRIORITY
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground capitalize">
                        {alert?.type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {alerts?.length > 0 && (
        <div className="p-6 border-t border-border">
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg flex gap-3">
            <Icon name="Info" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-foreground mb-1">Action Required</p>
              <p className="text-muted-foreground">
                {alerts?.length} member{alerts?.length > 1 ? 's' : ''} require immediate attention.
                Review and resolve access restrictions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CriticalAlertsPanel;